import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listContracts, deleteContract, duplicateContract } from "@/lib/contracts.functions";
import { ArrowLeft, FileText, Plus, Search, Copy, Trash2, Layers } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin_/contracts/")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Contracts — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: ContractsDashboard,
});

const TYPE_LABELS: Record<string, string> = {
  therapist_agreement: "Therapist",
  clinical_director_agreement: "Clinical Director",
  independent_contractor: "Independent Contractor",
  employee: "Employee",
  volunteer: "Volunteer",
  partnership: "Partnership",
  nda: "NDA",
  custom: "Custom",
};

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-muted text-foreground",
  pending_signature: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  expiring_soon: "bg-orange-100 text-orange-800",
  expired: "bg-red-100 text-red-800",
  cancelled: "bg-muted text-foreground",
  terminated: "bg-red-200 text-red-900",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_signature: "Pending Signature",
  active: "Active",
  expiring_soon: "Expiring Soon",
  expired: "Expired",
  cancelled: "Cancelled",
  terminated: "Terminated",
};

function ContractsDashboard() {
  const list = useServerFn(listContracts);
  const del = useServerFn(deleteContract);
  const dup = useServerFn(duplicateContract);
  const qc = useQueryClient();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [type, setType] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-contracts"],
    queryFn: () => list(),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-contracts"] }); toast.success("Contract deleted"); },
    onError: (e: Error) => toast.error(e.message),
  });
  const dupMut = useMutation({
    mutationFn: (id: string) => dup({ data: { id } }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["admin-contracts"] }); toast.success("Contract duplicated"); nav({ to: "/admin/contracts/$id", params: { id: res.id } }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = (data?.items ?? []).filter((c: any) => {
    if (status !== "all" && c.status !== status) return false;
    if (type !== "all" && c.type !== type) return false;
    if (q && !(`${c.title} ${c.counterparty_name}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>

        <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-foreground">Contracts</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage every Recovery Trauma™ contract from one place.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/contracts/templates" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted">
              <Layers className="h-4 w-4" /> Templates
            </Link>
            <Link to="/admin/contracts/new" className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
              <Plus className="h-4 w-4" /> New contract
            </Link>
          </div>
        </header>

        <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title or counterparty…"
              className="w-full rounded-lg border border-border bg-card py-2 ps-9 pe-3 text-sm"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="all">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="all">All types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="rounded-xl border border-border bg-card">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No contracts yet.</p>
              <Link to="/admin/contracts/new" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
                <Plus className="h-4 w-4" /> Create your first contract
              </Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-start text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Counterparty</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">End date</th>
                  <th className="px-4 py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c: any) => (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <Link to="/admin/contracts/$id" params={{ id: c.id }} className="font-medium text-foreground hover:underline">
                        {c.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.counterparty_name}
                      {c.counterparty_role ? <span className="text-xs"> · {c.counterparty_role}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{TYPE_LABELS[c.type] ?? c.type}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[c.status] ?? ""}`}>
                        {STATUS_LABELS[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.end_date ?? "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Link
                          to="/admin/contracts/$id"
                          params={{ id: c.id }}
                          className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
                          title={c.status === "draft" ? "Open to send for signing" : "Open contract"}
                        >
                          {c.status === "draft" || c.status === "pending_signature" ? "Open & Send" : "Open"}
                        </Link>
                        <button
                          onClick={() => dupMut.mutate(c.id)}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete "${c.title}"? This cannot be undone.`)) delMut.mutate(c.id); }}
                          className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
